/**
 * Forklift Inspection Component
 * ===============================
 *
 * Individual form component
 *
 * Base Components:
 * - EXTENDS BaseFormComponent<ForkliftInspection> for form logic inheritance
 * - USES FormLayoutComponent for step navigation UI (in template)
 *
 * Form Configuration:
 * - Form structure, steps, sections, and field organization are defined in the form model
 * - Signature and upload requirements are configured via form model metadata
 * - Component implements abstract methods: initializeForm() and getFormData()
 * - Component overrides setupDefaultStepValidation() for custom step validation rules
 *
 * Key Functionality:
 * - Creates nested form groups for logical organization (enginePower, mechanicalSafety, signOff)
 * - Implements comprehensive equipment inspection checklist with Yes/No/N/A options
 * - Provides optional comment fields for each inspection item
 * - Implements form validation for safety compliance
 * - Implements step validation with signature requirements for final step
 * - Flattens nested form structure for backend submission
 *
 * Form Model vs Component Responsibilities:
 * - FORM MODEL: Defines step/section structure, field mappings, attachment requirements, and form metadata
 * - COMPONENT: Handles Angular form validation, form group creation, conditional field logic, and overrides base form methods
 *
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseFormComponent } from '../../base-form.component';
import { FormStep } from '../../../../../models/forms/form-steps.interface';
import { VoluntaryRespiratorUse } from '../../../../../models/forms/voluntary-respirator/voluntary-respirator.model';
import { FormService } from '../../../../../services/form.service';
import { EntraService } from '../../../../../services/entra.service';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { SignatureService } from '../../../../../services/signature.service';
import { ToastService } from '../../../../../services/toast.service';
import { ScreenSizeService } from '../../../../../services/screen-size.service';
import { FormModelService } from '../../../../../services/form-model.service';

/**
 * Component for handling Voluntary Respirator Use submissions.
 * This form is used to document voluntary respirator use,
 * ensuring proper safety measures and compliance.
 */
@Component({
  selector: 'app-volunteer-respirator',
  templateUrl: './volunteer-respirator.component.html',
  styleUrls: ['./volunteer-respirator.component.css'],
})
export class VoluntaryRespiratorUseComponent
  extends BaseFormComponent<VoluntaryRespiratorUse>
  implements OnInit
{
  // Form type identifier used for routing and form metadata lookup
  override formType = VoluntaryRespiratorUse.getFormInfo().formType;

  // Instance of the form data model
  override model = new VoluntaryRespiratorUse();

  // Define the form steps and their properties
  override steps: FormStep[] = VoluntaryRespiratorUse.getFormSteps();

  public respiratorTypeOptions =
    VoluntaryRespiratorUse.getRespiratorTypeOptions();

    public regulatoryCard = VoluntaryRespiratorUse.getRegulatoryCardBlock();
    public purposeParagraph = VoluntaryRespiratorUse.getPurposeParagraphBlock();

  userFullName: string = '';

  constructor(
    protected override fb: FormBuilder,
    protected override formService: FormService,
    protected override router: Router,
    protected override entraService: EntraService,
    protected override attachmentService: AttachmentUploadService,
    protected override toastService: ToastService,
    protected override signatureService: SignatureService,
    protected override screenSizeService: ScreenSizeService,
    protected formModelService: FormModelService,
  ) {
    super(
      fb,
      formService,
      router,
      entraService,
      attachmentService,
      toastService,
      signatureService,
      screenSizeService,
    );

    // Initialize form metadata first
    this.formMetadata = this.formModelService.getFormMetadata(this.formType)!;
    this.userFullName = `${this.entraService.userFirstName()} ${this.entraService.userLastName()}`;
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  /**
   * Initializes the form with its controls and validators.
   * Sets up the form group with all required fields and their validation rules.
   */
  protected override initializeForm(): void {
    this.form = this.fb.group({
      details: this.fb.group({
        employeeName: [this.userFullName, Validators.required],
        jobTitle: [this.model.jobTitle, Validators.required],
        department: [this.model.department, Validators.required],
        respiratorType: [VoluntaryRespiratorUse.getRespiratorTypeOptions()[0], Validators.required],
        isPersonalProperty: [this.model.isPersonalProperty, Validators.required],
      }),

      acknowledgement: this.fb.group({
        signatureDate: [this.model.signatureDate, Validators.required],
      }),
    });
  }

  // Form group getters
  get respiratorDetailsFormGroup(): FormGroup {
    return this.form.get('details') as FormGroup;
  }

  get acknowledgementFormGroup(): FormGroup {
    return this.form.get('acknowledgement') as FormGroup;
  }

  /**
   * Retrieves the current form data and creates a new VoluntaryRespiratorUse instance.
   * This method is called when submitting the form.
   */
  protected override getFormData(): VoluntaryRespiratorUse {
    const formValue = this.form.value;
    // Flatten the form value if necessary (for nested structures)
    const flattenedData = {
      ...formValue.details,
      ...formValue.acknowledgement,
      // Add any additional fields from the form value as needed
    };
    // Add any additional processing or transformations here if needed

    const voluntaryRespiratorUse = new VoluntaryRespiratorUse(flattenedData);
    const updatedVoluntaryRespiratorUse = this.updateModelWithAttachments(
      voluntaryRespiratorUse,
    );
    return updatedVoluntaryRespiratorUse;
  }

  /**
   * Sets up validation rules for each step of the voluntary respirator use form.
   * Each step has specific validation requirements that must be met before proceeding:
   * - Respirator Details: Requires employee information and respirator details
   * - Acknowledgement: Requires employee signature and date
   *
   * The validation ensures that:
   * 1. All required fields are filled out
   * 2. Required signatures are collected
   * 3. Form data meets safety and compliance requirements
   */
  protected override setupDefaultStepValidation(): void {
    // Skip validation setup if noFormRequirements is true
    if (!this.shouldApplyFormRequirements()) {
      return;
    }
    // Define step indices
    const SIGNATURE_REQUIREMENT_INDEX: number = 0;

    // Custom validation rules for each step using the form groups directly
    this.setupStepValidation(
      'respirator-details',
      this.respiratorDetailsFormGroup,
    );
    this.setupStepValidation('acknowledgement', this.acknowledgementFormGroup, {
      signatureIndex: [SIGNATURE_REQUIREMENT_INDEX],
    });
  }
}
